import i18n from 'i18n'
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { deliveryZonesAPI, type DeliveryZonePayload, type DeliveryZoneRow } from 'api/deliveryZones'
import { isOfflineQueuedFulfilledAction } from 'helpers/offlineAction'
import { branchChanged } from './branchSlice'

interface DeliveryZonesState {
  data: DeliveryZoneRow[]
  loading: boolean
  error: string | null
}

const initialState: DeliveryZonesState = {
  data: [],
  loading: false,
  error: null,
}

function unwrapZoneList(response: unknown): DeliveryZoneRow[] {
  if (Array.isArray(response)) return response as DeliveryZoneRow[]
  if (response && typeof response === 'object' && Array.isArray((response as { data?: unknown }).data)) {
    return (response as { data: DeliveryZoneRow[] }).data
  }
  return []
}

function unwrapZone(response: unknown): DeliveryZoneRow | null {
  if (!response || typeof response !== 'object') return null
  const row = response as DeliveryZoneRow & { data?: DeliveryZoneRow }
  if (row.data && typeof row.data === 'object' && typeof row.data.id === 'string') {
    return row.data
  }
  if (typeof row.id === 'string') return row
  return null
}

export const fetchDeliveryZones = createAsyncThunk(
  'deliveryZones/fetchDeliveryZones',
  async (params: Record<string, unknown> | undefined, { rejectWithValue }) => {
    try {
      const response = await deliveryZonesAPI.getAll(params)
      return unwrapZoneList(response)
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || i18n.t('errors.deliveryZones.fetchFailed'))
    }
  },
)

export const createDeliveryZone = createAsyncThunk(
  'deliveryZones/createDeliveryZone',
  async (data: DeliveryZonePayload, { rejectWithValue }) => {
    try {
      const response = await deliveryZonesAPI.create(data)
      const zone = unwrapZone(response)
      if (!zone) {
        return rejectWithValue(i18n.t('errors.deliveryZones.createFailed'))
      }
      return zone
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || i18n.t('errors.deliveryZones.createFailed'))
    }
  },
)

export const updateDeliveryZone = createAsyncThunk(
  'deliveryZones/updateDeliveryZone',
  async ({ id, data }: { id: string; data: DeliveryZonePayload }, { rejectWithValue }) => {
    try {
      const response = await deliveryZonesAPI.update(id, data)
      const zone = unwrapZone(response)
      if (!zone) {
        return rejectWithValue(i18n.t('errors.deliveryZones.updateFailed'))
      }
      return zone
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || i18n.t('errors.deliveryZones.updateFailed'))
    }
  },
)

const deliveryZonesSlice = createSlice({
  name: 'deliveryZones',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(branchChanged, () => initialState)
      .addCase(fetchDeliveryZones.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchDeliveryZones.fulfilled, (state, action) => {
        state.loading = false
        state.data = Array.isArray(action.payload) ? action.payload : []
      })
      .addCase(fetchDeliveryZones.rejected, (state, action) => {
        state.loading = false
        state.error = (action.payload as string) || i18n.t('errors.deliveryZones.fetchFailed')
      })
      .addCase(createDeliveryZone.fulfilled, (state, action) => {
        if (isOfflineQueuedFulfilledAction(action)) return
        const created = action.payload
        if (!created?.id) return
        state.data.push(created)
      })
      .addCase(updateDeliveryZone.fulfilled, (state, action) => {
        if (isOfflineQueuedFulfilledAction(action)) return
        const updated = action.payload
        if (!updated?.id) return
        const idx = state.data.findIndex((z) => z.id === updated.id)
        if (idx !== -1) state.data[idx] = { ...state.data[idx], ...updated }
      })
  },
})

export default deliveryZonesSlice.reducer
